import React from 'react';
import { Paper, Box, Typography, Divider, Stack, Chip } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { cardThemeStyles as styles } from './cardStyle';

interface MaintenanceCardProps {
  activeContractEquipment: {
    next_maintenance?: string | null;
    last_closed_maintenance?: string | null;
  } | null;
  Spinner: React.ComponentType;
}

const getRemainingDays = (dateString?: string | null): number | null => {
  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextMaintenance = new Date(dateString);
  nextMaintenance.setHours(0, 0, 0, 0);
  const diffTime = nextMaintenance.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const MaintenanceCard: React.FC<MaintenanceCardProps> = ({
  activeContractEquipment,
  Spinner
}) => {
  if (!activeContractEquipment) return null;

  const daysRemaining = getRemainingDays(activeContractEquipment?.next_maintenance);

  return (
    <Paper elevation={1} sx={styles.cardPaper}>
      <Box sx={styles.headerContainer}>
        <Box sx={styles.iconCircle}>
          <CalendarMonthIcon />
        </Box>
        <Typography variant="h6" sx={styles.headerTitle}>
          Manutenzione
        </Typography>
      </Box>

      {activeContractEquipment ? (
        <Stack spacing={2} divider={<Divider flexItem />}>
          <Box sx={styles.flexRowSpaceBetween}>
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">
                Ultima Manutenzione
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {activeContractEquipment?.last_closed_maintenance ?? 'Nessuna presente'}
              </Typography>
            </Box>
            {activeContractEquipment?.last_closed_maintenance && (
              <Chip label="Completata" size="small" sx={styles.badgeChip(false)} />
            )}
          </Box>

          <Box sx={styles.flexRowSpaceBetween}>
            <Box>
              <Typography variant="caption" color="text.secondary" component="div">
                Prossima Manutenzione
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {activeContractEquipment?.next_maintenance ?? 'Nessuna presente'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {activeContractEquipment.next_maintenance && (
                <Chip label="In scadenza" size="small" sx={styles.badgeChip(true)} />
              )}
              {daysRemaining !== null && (
                <Typography
                  variant="caption"
                  color={daysRemaining < 0 ? 'error' : 'text.secondary'}>
                  {daysRemaining < 0
                    ? `Scaduto da ${Math.abs(daysRemaining)} ${
                        Math.abs(daysRemaining) === 1 ? 'giorno' : 'giorni'
                      }`
                    : `Scadenza fra ${daysRemaining} ${daysRemaining === 1 ? 'giorno' : 'giorni'}`}
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Spinner />
        </Box>
      )}
    </Paper>
  );
};
